<template>
    <div class="home-page">
        <!-- Hero Slider -->
        <HeroSlider :sliders="sliders" />
        
        <!-- Contenido Destacado (desde Blogs con mostrar_en_inicio) -->
        <section class="blog-section" v-if="featuredBlogs && featuredBlogs.length">
            <div class="section-header">
                <h2 class="section-title">Contenido Destacado</h2>
                <p class="section-subtitle">Artículos, noticias y recursos seleccionados para ti</p>
            </div>
            
            <div class="blog-grid">
                <article class="blog-card" v-for="blog in featuredBlogs" :key="blog.id">
                    <div class="blog-image-wrapper">
                        <img 
                            :src="blog.imagen ? `/storage/${blog.imagen}` : 'https://via.placeholder.com/800x600/1e3a5f/d4af37?text=Blog'" 
                            :alt="blog.titulo" 
                            class="blog-image"
                        >
                        <span class="content-category">Blog</span>
                        <span class="blog-date">{{ formatDate(blog.created_at) }}</span>
                    </div>
                    <div class="blog-content">
                        <h3 class="blog-title">{{ blog.titulo }}</h3>
                        <p class="blog-excerpt">{{ truncate(blog.descripcion, 120) }}</p>
                        <router-link :to="`/blog/${blog.id}/${blog.slug}`" class="blog-link">
                            Leer más <i class="fas fa-arrow-right"></i>
                        </router-link>
                    </div>
                </article>
            </div>
        </section>
        
        <!-- Featured Video Section -->
        <section class="video-section" v-if="featuredVideo">
            <div class="section-header">
                <h2 class="section-title">Conoce Nuestros Equipos</h2>
                <p class="section-subtitle">Tecnología POS de vanguardia para tu negocio</p>
            </div>
                
            <div class="video-container">
                <div class="video-wrapper">
                    <iframe 
                        :src="`https://www.youtube.com/embed/${featuredVideo.video_id}`"
                        frameborder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowfullscreen
                    ></iframe>
                </div>
            </div>
        </section>
        
        <!-- Partners Section con fondo diferente -->
        <section class="partners-section" v-if="partners && partners.length">
            <div class="section-header">
                <h2 class="section-title">Nuestros Aliados</h2>
                <p class="section-subtitle">Trabajamos con las mejores empresas del sector</p>
            </div>
            
            <div class="partners-grid">
                <div 
                    v-for="partner in partners" 
                    :key="partner.id"
                    class="partner-logo"
                >
                    <img 
                        v-if="partner.logo" 
                        :src="`/storage/${partner.logo}`" 
                        :alt="partner.nombre"
                    >
                    <span v-else class="partner-placeholder">{{ partner.nombre }}</span>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import HeroSlider from '../components/home/HeroSlider.vue';
import api from '../services/api';

export default {
    name: 'Home',
    components: {
        HeroSlider
    },
    data() {
        return {
            featuredVideo: null,
            featuredBlogs: [],
            sliders: []
        };
    },
    computed: {
        ...mapState(['config', 'partners'])
    },
    async created() {
        await this.loadData();
    },
    methods: {
        async loadData() {
            try {
                // Load sliders
                const slidersResponse = await api.getSliders();
                this.sliders = slidersResponse.data || [];
                
                // Load featured video
                const videoResponse = await api.getFeaturedVideo();
                if (videoResponse.data) {
                    this.featuredVideo = videoResponse.data;
                }
                
                // Load featured blogs (mostrar_en_inicio = true)
                const blogsResponse = await api.getHomepageBlogs();
                this.featuredBlogs = blogsResponse.data || [];
            } catch (error) {
                console.error('Error loading home data:', error);
            }
        },
        getYoutubeId(url) {
            if (!url) return null;
            const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
            const match = url.match(regExp);
            return (match && match[2].length === 11) ? match[2] : null;
        },
        formatDate(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString);
            const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
            return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
        },
        truncate(text, length) {
            if (!text) return '';
            return text.length > length ? text.substring(0, length) + '...' : text;
        }
    }
};
</script>

<style scoped>
.home-page {
    background-color: var(--background);
    width: 100%;
}

/* BLOG/CONTENT SECTION */
.blog-section {
    padding: 100px 2rem;
    background: linear-gradient(180deg, transparent, rgba(30, 58, 95, 0.1));
}

.section-header {
    text-align: center;
    margin-bottom: 4rem;
}

.section-title {
    font-family: 'Playfair Display', serif;
    font-size: 3rem;
    font-weight: 700;
    margin-bottom: 1rem;
    background: linear-gradient(135deg, var(--text-light), var(--accent-gold));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

.section-subtitle {
    font-size: 1.2rem;
    color: var(--text-gray);
    font-weight: 300;
}

.blog-grid {
    max-width: 1400px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    gap: 2rem;
}

.blog-card {
    background: var(--card-bg);
    border-radius: 20px;
    overflow: hidden;
    transition: all 0.4s ease;
    border: 1px solid rgba(212, 175, 55, 0.1);
    display: flex;
    flex-direction: column;
    height: 100%;
}

.blog-card:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 60px rgba(212, 175, 55, 0.2);
    border-color: var(--accent-gold);
}

.blog-image-wrapper {
    position: relative;
    overflow: hidden;
    height: 250px;
}

.blog-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
}

.blog-card:hover .blog-image {
    transform: scale(1.1);
}

.content-category {
    position: absolute;
    top: 20px;
    left: 20px;
    background: var(--accent-teal);
    color: var(--primary-dark);
    padding: 0.5rem 1.2rem;
    border-radius: 50px;
    font-size: 0.8rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    z-index: 1;
}

.blog-date {
    position: absolute;
    bottom: 20px;
    right: 20px;
    background: rgba(10, 22, 40, 0.8);
    color: var(--text-light);
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-size: 0.85rem;
    font-weight: 500;
}

.blog-content {
    padding: 2rem;
    flex: 1;
    display: flex;
    flex-direction: column;
}

.blog-title {
    font-family: 'Playfair Display', serif;
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--text-light);
    margin-bottom: 1rem;
    line-height: 1.3;
}

.blog-excerpt {
    color: var(--text-gray);
    line-height: 1.6;
    margin-bottom: 1.5rem;
    flex: 1;
}

.blog-link {
    color: var(--accent-gold);
    text-decoration: none;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    transition: gap 0.3s ease;
}

.blog-link:hover {
    gap: 1rem;
}

.blog-link i {
    font-size: 0.875rem;
}

/* VIDEO SECTION */
.video-section {
    padding: 100px 2rem;
    max-width: 1400px;
    margin: 0 auto;
}

.video-container {
    position: relative;
    max-width: 1100px;
    margin: 0 auto;
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
    border: 2px solid rgba(212, 175, 55, 0.2);
}

.video-wrapper {
    position: relative;
    padding-bottom: 56.25%;
    height: 0;
    overflow: hidden;
}

.video-wrapper iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
}

/* PARTNERS SECTION - Fondo diferente y elegante */
.partners-section {
    padding: 100px 2rem;
    background: linear-gradient(
        135deg,
        rgba(10, 22, 40, 1) 0%,
        rgba(15, 25, 45, 0.98) 50%,
        rgba(10, 22, 40, 1) 100%
    );
    position: relative;
}

.partners-section::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: 
        radial-gradient(circle at 25% 25%, rgba(212, 175, 55, 0.04) 0%, transparent 50%),
        radial-gradient(circle at 75% 75%, rgba(45, 212, 191, 0.04) 0%, transparent 50%);
    pointer-events: none;
}

.partners-section .section-header {
    position: relative;
    z-index: 1;
}

.partners-grid {
    max-width: 1400px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 2rem;
    align-items: center;
    position: relative;
    z-index: 1;
}

.partner-logo {
    background: rgba(30, 41, 59, 0.6);
    backdrop-filter: blur(10px);
    padding: 2.5rem;
    border-radius: 15px;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 140px;
    border: 1px solid rgba(212, 175, 55, 0.15);
    transition: all 0.4s ease;
}

.partner-logo:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 40px rgba(212, 175, 55, 0.25);
    border-color: var(--accent-gold);
    background: rgba(30, 41, 59, 0.8);
}

.partner-logo img {
    max-width: 100%;
    max-height: 80px;
    object-fit: contain;
    filter: grayscale(100%) brightness(1.2);
    opacity: 0.7;
    transition: all 0.4s ease;
}

.partner-logo:hover img {
    filter: grayscale(0%) brightness(1);
    opacity: 1;
}

.partner-placeholder {
    color: var(--text-gray);
    font-size: 1.5rem;
    font-weight: 600;
}

/* RESPONSIVE */
@media (max-width: 768px) {
    .blog-section,
    .video-section,
    .partners-section {
        padding: 60px 1rem;
    }
    
    .section-title {
        font-size: 2.2rem;
    }
    
    .section-subtitle {
        font-size: 1rem;
    }
    
    .blog-grid {
        grid-template-columns: 1fr;
    }
    
    .partners-grid {
        grid-template-columns: repeat(auto-fit, minmin(150px, 1fr));
        gap: 1.5rem;
    }
}
</style>